/** /status — runtime health surface for the Memso operator (Waqas
 *  primarily). Renders one row per dependency probed by
 *  GET /api/status/full. Polls every 10 s while open so the page
 *  feels live without hammering the backend.
 *
 *  The bar at the top folds every row into a single traffic light:
 *  green if all rows are green, yellow if any row is yellow, red
 *  if any row is red. Same idea as an SRE oncall dashboard.
 *
 *  The bottom card cross-checks the running bundle against the
 *  daemon-reported gitSha. That's the same comparison UpdateBanner
 *  uses to decide whether to interrupt with a refresh prompt, but
 *  on /status we show it as a row that surfaces both sides + the
 *  daemon build timestamp so the operator can debug "wait, which
 *  bundle am I on?" without DevTools. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Circle, ArrowRight } from "lucide-react";
import { fetchServerVersion } from "../lib/version";
import { TeachingBanner } from "../components/TeachingBanner";
import { api, type CurationSurfaceScore } from "../lib/api";

type StatusState = "green" | "yellow" | "red" | "unknown";

type StatusItem = {
  id: string;
  label: string;
  state: StatusState;
  latencyMs: number | null;
  hint: string;
  detail?: Record<string, unknown>;
};

type WakiAiRelay = {
  last_ping_ms: number | null;
  last_ping_ok: boolean | null;
  last_ping_at: string | null;
  last_410_at: string | null;
  last_410_model: string | null;
  last_410_feature_id: string | null;
  last_410_fallback_to: string | null;
  url: string;
};

type StatusResponse = {
  overall: StatusState;
  items: StatusItem[];
  wakiai_relay?: WakiAiRelay;
  tookMs: number;
  ts: string;
};

/** Builds and reads the bundle's own version stamp. Same approach
 *  the UpdateBanner uses — the constants are inlined by vite at
 *  build time via the `define:` block in vite.config.ts. Vite emits
 *  __APP_VERSION__ as "<pkgVersion>+<gitSha>" (see vite.config.ts:73)
 *  and __BUILD_TIME__ as the build timestamp. We snapshot them at
 *  module load so the page reflects "what JS shipped with this tab",
 *  not the latest poll. */
declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;
const BUNDLE_VERSION: string = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown";
const BUNDLE_BUILD_TIME: string = typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : "unknown";
/** Pull the sha tail off "<pkgVersion>+<gitSha>". Returns "unknown" if
 *  the bundle version is missing or in an unexpected shape. */
function parseBundleSha(v: string): string {
  const plus = v.lastIndexOf("+");
  return plus >= 0 && plus < v.length - 1 ? v.slice(plus + 1) : "unknown";
}
const BUNDLE_GIT_SHA: string = parseBundleSha(BUNDLE_VERSION);

const POLL_INTERVAL_MS = 10_000;

function StateIcon({ state, className = "h-5 w-5" }: { state: StatusState; className?: string }) {
  if (state === "green") return <CheckCircle2 className={`${className} text-emerald-500`} />;
  if (state === "yellow") return <AlertTriangle className={`${className} text-amber-500`} />;
  if (state === "red") return <XCircle className={`${className} text-rose-500`} />;
  return <Circle className={`${className} text-slate-400`} />;
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "now";
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

export function Status() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [serverVersion, setServerVersion] = useState<{ version: string; gitSha: string | null; buildTime: string | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [statusRes, versionRes] = await Promise.allSettled([
          fetch("/api/status/full", { credentials: "same-origin", cache: "no-store" }).then((r) => {
            if (!r.ok) throw new Error(`/api/status/full responded ${r.status}`);
            return r.json() as Promise<StatusResponse>;
          }),
          fetchServerVersion(),
        ]);
        if (cancelled) return;
        if (statusRes.status === "fulfilled") {
          setData(statusRes.value);
          setLastFetched(new Date().toISOString());
          setErr(null);
        } else {
          setErr(statusRes.reason instanceof Error ? statusRes.reason.message : String(statusRes.reason));
        }
        if (versionRes.status === "fulfilled" && versionRes.value) {
          setServerVersion({
            version: versionRes.value.version,
            gitSha: versionRes.value.gitSha ?? null,
            buildTime: versionRes.value.buildTime ?? null,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const overall = err ? "red" : (data?.overall ?? "unknown");
  const overallLabel: Record<StatusState, string> = {
    green: "All systems normal",
    yellow: "Degraded — some dependencies need attention",
    red: "Critical — at least one dependency is down",
    unknown: "Checking…",
  };

  const bundleSha = BUNDLE_GIT_SHA.slice(0, 7);
  const serverSha = serverVersion?.gitSha ? serverVersion.gitSha.slice(0, 7) : "?";
  const bundleVsServer: StatusState =
    !serverVersion ? "unknown"
    : bundleSha === serverSha ? "green"
    : "yellow";
  const bundleVsServerHint =
    !serverVersion ? "Daemon version unreachable."
    : bundleSha === serverSha
      ? `Bundle and daemon both on ${serverSha}.`
      : `Bundle is on ${bundleSha} but daemon is on ${serverSha}. Reload this tab to pick up the new bundle.`;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 text-slate-900 dark:text-slate-100">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Status</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Live read-out of every dependency the Memso shell connects to. Updates every 10 seconds.
          </p>
        </div>
        <button
          type="button"
          onClick={() => location.reload()}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          aria-label="Reload page"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Reload
        </button>
      </header>

      <div className="mb-4">
        <TeachingBanner id="status_overview" />
      </div>

      <section
        className={`mb-6 flex items-center gap-3 rounded-lg border p-4 ${
          overall === "green"
            ? "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950"
            : overall === "yellow"
              ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950"
              : overall === "red"
                ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950"
                : "border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
        }`}
      >
        <StateIcon state={overall} className="h-7 w-7" />
        <div className="flex-1">
          <div className="text-base font-medium">{overallLabel[overall]}</div>
          <div className="text-xs text-slate-600 dark:text-slate-400">
            Last refresh {relTime(lastFetched)} · backend probe took{" "}
            {data?.tookMs != null ? `${data.tookMs} ms` : "—"}
          </div>
        </div>
      </section>

      {err && (
        <div className="mb-6 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
          Status endpoint failed: {err}.{" "}
          <Link to="/" className="underline">
            Return to Memso
          </Link>
          .
        </div>
      )}

      <ul className="mb-6 divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {/* Bundle vs daemon SHA row. Synthesised client-side because
            both halves of the comparison live in different probe
            results, and we want them side-by-side on the same row. */}
        <li className="flex items-start gap-3 p-3">
          <StateIcon state={bundleVsServer} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <div className="truncate text-sm font-medium">Bundle in sync with daemon</div>
              <div className="text-xs tabular-nums text-slate-500 dark:text-slate-400">—</div>
            </div>
            <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{bundleVsServerHint}</div>
            <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-2 text-[10px] leading-snug text-slate-700 dark:bg-slate-800 dark:text-slate-200">
{`bundle: ${BUNDLE_VERSION} (sha ${bundleSha}) built ${BUNDLE_BUILD_TIME}
daemon: ${serverVersion?.version ?? "?"} (sha ${serverSha}) built ${serverVersion?.buildTime ?? "?"}`}
            </pre>
          </div>
        </li>

        {data?.items.map((item) => (
          <li key={item.id} className="flex items-start gap-3 p-3">
            <StateIcon state={item.state} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="truncate text-sm font-medium">{item.label}</div>
                <div className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
                  {item.latencyMs != null ? `${item.latencyMs} ms` : "—"}
                </div>
              </div>
              <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{item.hint}</div>
              {item.detail && Object.keys(item.detail).length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-300">
                    detail
                  </summary>
                  <pre className="mt-1 overflow-x-auto rounded bg-slate-100 p-2 text-[10px] leading-snug text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {JSON.stringify(item.detail, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </li>
        ))}

        {loading && !data && (
          <li className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">Probing dependencies…</li>
        )}
      </ul>

      {/* WakiAI relay teaching line. Two-channel observation: the
          WakiAI Service row above reports live reachability via
          /health; this card surfaces the boot-time ping + the most
          recent eviction (410-shape) so the operator can see "did
          the relay come up cleanly, and has any call been kicked
          over to local step-down since?" without grepping the
          daemon log. */}
      {data?.wakiai_relay && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-baseline justify-between">
            <div className="text-sm font-medium">Local AI relay</div>
            <div className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
              {data.wakiai_relay.last_ping_ms != null
                ? `boot ping ${data.wakiai_relay.last_ping_ms} ms`
                : "boot ping pending"}
            </div>
          </div>
          <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            Local AI relay. Memso falls back to cloud if a model gets evicted here.
          </div>
          {data.wakiai_relay.last_410_at && (
            <div className="mt-2 rounded bg-amber-50 p-2 text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Most recent eviction {relTime(data.wakiai_relay.last_410_at)} on{" "}
              <span className="font-medium">{data.wakiai_relay.last_410_model ?? "?"}</span>
              {data.wakiai_relay.last_410_feature_id ? (
                <> for {data.wakiai_relay.last_410_feature_id}</>
              ) : null}
              {data.wakiai_relay.last_410_fallback_to ? (
                <> · routed to {data.wakiai_relay.last_410_fallback_to}</>
              ) : null}
              .
            </div>
          )}
        </section>
      )}

      <CurationScorecard />

      <footer className="mt-8 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
          <ArrowRight className="h-3 w-3 rotate-180" /> back to Memso
        </Link>
        <span>Auto-refresh every {Math.round(POLL_INTERVAL_MS / 1000)} s</span>
      </footer>
    </div>
  );
}

/** Best-effort surface → in-app route so each scorecard row links to the
 *  surface it scores. Unmapped surfaces render as plain text. */
const SURFACE_LINK: Record<string, string> = {
  today_clusters: "/now",
  recommendations: "/recommendations",
  engagements: "/engagements",
  awaiting_you: "/engagements",
  observations: "/observations",
  recall: "/recall",
  library: "/explore/library",
  people: "/people",
  trip: "/trips",
};

function scoreClasses(score: number): string {
  if (score >= 3) return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
  if (score === 2) return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
  return "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200";
}

function componentTooltip(components: { label: string; met: boolean }[]): string {
  return components.map((c) => `${c.met ? "✓" : "✗"} ${c.label}`).join("\n");
}

function ScoreCell({
  score,
  components,
  href,
}: {
  score: number;
  components: { label: string; met: boolean }[];
  href: string | undefined;
}) {
  const cell = (
    <span
      title={componentTooltip(components)}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold tabular-nums ${scoreClasses(score)}`}
    >
      {score}
    </span>
  );
  return (
    <td className="px-2 py-2 text-center">
      {href ? (
        <Link to={href} aria-label={`Open surface, score ${score} of 3`}>
          {cell}
        </Link>
      ) : (
        cell
      )}
    </td>
  );
}

/** W012 — per-surface Curate / Dynamic / Learning scorecard. Each cell
 *  is 0-3; a 1/1/1 row is a HIGH finding even when the surface "works".
 *  Tooltip on each cell explains the three score components; cells link
 *  to the surface. Recompute button rebakes on demand (the nightly
 *  weights rebuild also refreshes it). */
function CurationScorecard() {
  const [rows, setRows] = useState<CurationSurfaceScore[]>([]);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    api
      .curationScorecard()
      .then((d) => {
        setRows(d.surfaces ?? []);
        setComputedAt(d.computed_at);
        setErr(null);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(load, []);

  const recompute = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const d = await api.recomputeCurationScorecard();
      setRows(d.surfaces ?? []);
      setComputedAt(d.computed_at);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <div>
          <h2 className="text-base font-medium">Curation scorecard</h2>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Each surface scored 0-3 on Curate / Dynamic / Learning. Hover a cell for the components.
            {computedAt ? ` Computed ${relTime(computedAt)}.` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={recompute}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          Recompute
        </button>
      </div>

      {err && (
        <div className="mb-3 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {err}
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <th className="px-2 py-1 font-medium">Surface</th>
            <th className="px-2 py-1 text-center font-medium">Curate</th>
            <th className="px-2 py-1 text-center font-medium">Dynamic</th>
            <th className="px-2 py-1 text-center font-medium">Learning</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row) => {
            const href = SURFACE_LINK[row.surface];
            return (
              <tr key={row.surface}>
                <td className="px-2 py-2">
                  {href ? (
                    <Link to={href} className="font-medium text-sky-700 hover:underline dark:text-sky-300">
                      {row.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{row.name}</span>
                  )}
                  <div className="text-[10px] text-slate-400">{row.surface}</div>
                </td>
                <ScoreCell score={row.curate} components={row.components.curate} href={href} />
                <ScoreCell score={row.dynamic} components={row.components.dynamic} href={href} />
                <ScoreCell score={row.learning} components={row.components.learning} href={href} />
              </tr>
            );
          })}
          {rows.length === 0 && !err && (
            <tr>
              <td colSpan={4} className="px-2 py-4 text-center text-xs text-slate-500 dark:text-slate-400">
                No curated surfaces scored yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
